import { apiGet, API_BASE } from './api';

/**
 * THE one definition of *how the order pickers' reference lists are loaded, and
 * how a failure or an empty department is represented* — for all four surfaces
 * that load them: `pages/NewOrder.js`, `pages/Approvals.js`,
 * `hooks/useMyOrderExpansion.js` and `hooks/useDeptOrderExpansion.js`.
 *
 * Extracted 2026-10-03 (owner: *"audit … where we should/could be using common
 * code"* → *"lets do them all"*). Before this there were FOUR copies of each
 * fetch, each with its own non-ok handling, its own failure sentinel and its own
 * empty-department clear — and they had already drifted on five axes.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 WHAT THIS MODULE IS *NOT*: IT IS NOT "ONE LOADER FOR ALL FOUR SURFACES",
 * AND THE NARROWER INVARIANT IS DELIBERATE.
 * ═══════════════════════════════════════════════════════════════════════════
 * Five differences between the four copies were measured, and **every one of
 * them is a kept option rather than a thing to unify** (owner ruling via the
 * controller, 2026-10-03: *"Use the narrower honest invariant and keep every
 * deliberate difference as an option"*). What is shared is the URL, the
 * non-ok/rejection handling and the failure REPRESENTATION. What is NOT shared
 * is WHEN a surface loads, WHETHER it loads, and WHICH lists it loads:
 *
 *   1. **Approvals fetches FOUR lists, the two hooks fetch FIVE.** Approvals
 *      has never fetched `/api/suppliers/name-suggestions`, so its picker
 *      offers records only. Its own comment calls that *"A real divergence, not
 *      an omission here"* — so `fetchSupplierSuggestionsOrNull` simply is not
 *      in its list. Do not add it to even the counts up.
 *   2. **The `referenceArmed` latch (`deferReferenceData`) is the two hooks'
 *      alone.** The hires Table mounts a hook for a table that is usually never
 *      expanded. An order page loads on mount. That is a CALLER decision and
 *      this module never sees it.
 *   3. **Dept splits cost codes and payment types into TWO effects; My Orders
 *      puts both in ONE.** Consequence, preserved: Dept's empty-department
 *      branch clears one flag per effect, My Orders' clears both in one write.
 *      `clearPickerLists` takes a LIST for exactly that reason.
 *   4. **`NewOrder` alone gates on `canLoadAccountingPickers` and wraps both
 *      accounting calls in `useLatestRequest`.** The gate is an authority
 *      question only the create form asks; the stale-response guard was added
 *      by an R14 review because a department switch mid-flight could offer
 *      Costume's cost codes on an order being raised in SFX. `isCurrent` is
 *      therefore an OPTIONAL argument — absent, there is no guard, which is
 *      byte-identical to what the other three did. 🚫 **Do not "finish the
 *      job" by adding a guard to the other three, and do not remove this one.**
 *      Both are behaviour changes and both are the owner's to rule on.
 *   5. **`NewOrder` reports a productions failure as a hard `loadError`** and
 *      the hooks report it as nothing at all (`[]`, no flag). Hence the
 *      `…OrThrow` / `…OrEmpty` pairs below: the CALLER owns what a failure
 *      means to its screen, this module owns what a failure IS.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 THE TWO RULES THAT EARN THE EXTRACTION
 * ═══════════════════════════════════════════════════════════════════════════
 * **(a) AN EMPTIED OPTION LIST IS NOT A COSMETIC LOSS**, and this was written
 * out in all four copies. A controlled `<select value={item.cost_code_id}>`
 * with no matching `<option>` falls back to `selectedIndex = 0` — the literal
 * `-- None --` — so every coded line reads UNCODED, and a manager reaching for
 * the dropdown to correct it can only pick `-- None --`, which
 * `PATCH /:id/items/:itemId/cost-code` then writes as `null` OVER A REAL CODE.
 * That is why the accounting fetchers **throw on a non-ok response**: a refusal
 * and a rejection must take the same path, or a 403 sets the flag on one branch
 * and not the other.
 *
 * **(b) `null` IS THE SUPPLIER FAILURE SENTINEL AND `[]` CANNOT BE.** `[]` is
 * exactly what a department with no supplier records legitimately returns, and
 * telling those two apart is the whole point. A non-ok response reaches the
 * sentinel by the same route a rejection does — `r.ok ? … : null` covers the
 * refusal, `.catch(() => null)` the rejection and a malformed body.
 * 🪤 **Each supplier call catches for ITSELF.** A shared `.catch` on the
 * caller's `Promise.all` fires once for the whole batch, so before the
 * per-entry catches every list was silently abandoned when any one rejected.
 *
 * 🔑 **Records and suggestions report under ONE `suppliers` key** — the two
 * tiers merge into one picker (`utils/supplierOptions.js`), so *"the list in
 * front of you is incomplete"* is the only distinction the person typing a
 * supplier name could act on. `suppliersFailed(...)` is variadic for that: the
 * hooks pass both values, Approvals passes the one it fetches.
 *
 * ⚠️ Pure transport + state-shape rules. No UI, no toast, no state of its own,
 * and **no authority decision** — every surface's gate stays at its own call
 * site, where it already lived.
 */

// ── THE FETCHERS — one definition of each URL and each failure representation ─

/**
 * Cost codes for ONE department. THROWS on a non-ok response so a refusal and a
 * rejection take the same path — see rule (a) above.
 */
export function fetchCostCodesActive(deptId) {
  return apiGet(`${API_BASE}/api/budget/cost-codes/active?department_id=${deptId}`)
    .then((r) => { if (!r.ok) throw new Error('Failed to load cost codes'); return r.json(); });
}

/**
 * Payment types for ONE department. Same shape as cost codes — read that block.
 * Tracked separately because these are two requests and either can be refused
 * alone.
 */
export function fetchPaymentTypesActive(deptId) {
  return apiGet(`${API_BASE}/api/budget/payment-types/active?department_id=${deptId}`)
    .then((r) => { if (!r.ok) throw new Error('Failed to load payment types'); return r.json(); });
}

/** Productions, degrading to `[]` — the expanded row's tier (no failure flag). */
export function fetchProductionsOrEmpty() {
  return apiGet(`${API_BASE}/api/productions`).then((r) => (r.ok ? r.json() : []));
}

/** Productions, THROWING — `NewOrder` turns this into a visible `loadError`. */
export function fetchProductionsOrThrow() {
  return apiGet(`${API_BASE}/api/productions`)
    .then((r) => { if (!r.ok) throw new Error('Failed to load productions'); return r.json(); });
}

/** Supplier RECORDS, degrading to the `null` sentinel — see rule (b) above. */
export function fetchSuppliersOrNull() {
  return apiGet(`${API_BASE}/api/suppliers`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/** Supplier RECORDS, THROWING — `NewOrder` flags `suppliers` from its catch. */
export function fetchSuppliersOrThrow() {
  return apiGet(`${API_BASE}/api/suppliers`)
    .then((r) => { if (!r.ok) throw new Error('Failed to load suppliers'); return r.json(); });
}

/**
 * Supplier-name SUGGESTIONS — the second tier, names typed on past approved
 * orders, never written to `suppliers` on any path. Same `null` sentinel.
 * 🔴 `pages/Approvals.js` deliberately does not call this (difference 1).
 */
export function fetchSupplierSuggestionsOrNull() {
  return apiGet(`${API_BASE}/api/suppliers/name-suggestions`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

/** Supplier suggestions, THROWING — `NewOrder`'s variant. */
export function fetchSupplierSuggestionsOrThrow() {
  return apiGet(`${API_BASE}/api/suppliers/name-suggestions`)
    .then((r) => { if (!r.ok) throw new Error('Failed to load supplier suggestions'); return r.json(); });
}

/**
 * Sub-departments, degrading to `[]`. `deptId` NARROWS to one department when
 * given; omitted (or null) the server applies the caller's own auto-scope.
 * 🪤 Only `NewOrder` passes one — the other three ask unscoped, deliberately.
 */
export function fetchSubDepartments(deptId) {
  const url = deptId
    ? `${API_BASE}/api/sub-departments?department_id=${deptId}`
    : `${API_BASE}/api/sub-departments`;
  return apiGet(url).then((r) => (r.ok ? r.json() : []));
}

/**
 * Gags for the Job / Rig / Gag autocomplete, degrading to `{ gags: [] }` — the
 * RESPONSE shape, not the array, because that is what the callers destructure.
 */
export function fetchGagsOrEmpty() {
  return apiGet(`${API_BASE}/api/gags`).then((r) => (r.ok ? r.json() : { gags: [] }));
}

// ── THE FAILURE / EMPTY-DEPARTMENT REPRESENTATION ────────────────────────────

/**
 * Did the supplier picker come back incomplete? Variadic over the `null`
 * sentinel so one call answers for however many tiers a surface fetched —
 * see the ONE-`suppliers`-key rule above.
 */
export function suppliersFailed(...values) {
  return values.some((v) => v === null);
}

/**
 * Run one accounting fetch and apply the house success/failure representation:
 * on success the list plus `loadFailures[failureKey] = false`; on ANY failure
 * an EMPTY list plus `loadFailures[failureKey] = true`.
 *
 * `isCurrent` is the OPTIONAL `useLatestRequest` predicate (difference 4).
 * Absent, there is no sequencing guard — byte-identical to the three surfaces
 * that never had one. Present, BOTH the success and the failure branch are
 * gated, because a stale rejection painting "couldn't be loaded" over good data
 * is the same defect as a stale success.
 */
export function loadPickerList({ fetchList, setList, setLoadFailures, failureKey, isCurrent }) {
  return fetchList()
    .then((data) => {
      if (isCurrent && !isCurrent()) return;
      setList(data);
      setLoadFailures((prev) => ({ ...prev, [failureKey]: false }));
    })
    .catch(() => {
      if (isCurrent && !isCurrent()) return;
      setList([]);
      setLoadFailures((prev) => ({ ...prev, [failureKey]: true }));
    });
}

/**
 * THE empty-department (or gated, or still-loading) branch: empty the lists AND
 * CLEAR their failure flags.
 *
 * 🪤 **CLEARING THE FLAG IS NOT OPTIONAL AND ALL FOUR COPIES SAID SO.** This
 * branch empties the list DELIBERATELY, so a failure flag carried across the
 * switch would print *"couldn't be loaded"* over a list that was never meant to
 * be fetched.
 *
 * `lists` is an ARRAY so the whole clear is ONE `setLoadFailures` write
 * whatever its length — which is what keeps My Orders' two-key clear and
 * Department Orders' one-key-per-effect clear both exact (difference 3).
 */
export function clearPickerLists({ setLoadFailures, lists }) {
  lists.forEach(({ setList }) => setList([]));
  setLoadFailures((prev) => {
    const next = { ...prev };
    lists.forEach(({ failureKey }) => { next[failureKey] = false; });
    return next;
  });
}

// ── THE TWO WHOLE-EFFECT BODIES ──────────────────────────────────────────────
// 🔑 These two exist because extracting only the FETCHERS left the effect
// SHELLS duplicated, and `npm run check-duplication` scored the residue at
// 0.85–0.97 over 46–93 normalised tokens and BLOCKED the commit — correctly.
// The house rule is explicit that the middle ground does not exist: a manifest
// entry is debt, not a fix. So the shells are shared too, and every surface
// difference is an ARGUMENT rather than a copy.

/** `failureKey` → its fetcher. Keeps the two accounting lists' URLs in one map. */
const ACCOUNTING_FETCHERS = {
  costCodes: fetchCostCodesActive,
  paymentTypes: fetchPaymentTypesActive,
};

/**
 * THE whole per-department accounting-picker load: the empty/gated branch and
 * one `loadPickerList` per list, in the order given.
 *
 * `departmentId` falsy (no active department, still loading, or the caller's
 * own capability gate refusing) → `clearPickerLists` and stop. That is ONE
 * `setLoadFailures` write for however many lists the caller passed, which is
 * what keeps every surface's clear exact.
 *
 * `entries` is `[{ setList, failureKey, isCurrent? }]`. Per-entry `isCurrent`
 * (not per-call) because `pages/NewOrder.js` holds a SEPARATE `useLatestRequest`
 * sequence per list — two requests, either refusable alone, so either staleable
 * alone.
 *
 * ⚠️ **This runs the lists in ONE effect, and three surfaces previously used
 * two.** Behaviour-equivalent and checked rather than assumed: the requests are
 * issued in `entries` order, so the request COUNT and ORDER are unchanged (the
 * thing `DepartmentOrders.expandEdit.test.js` §4 counts); the only difference is
 * that the clear path became one state write instead of two, which React
 * batched anyway. The two effects always shared a dependency array, so there was
 * never a render in which one had run and the other had not.
 */
export function loadAccountingLists({ departmentId, entries, setLoadFailures }) {
  if (!departmentId) {
    clearPickerLists({ setLoadFailures, lists: entries });
    return;
  }
  entries.forEach(({ setList, failureKey, isCurrent }) => {
    loadPickerList({
      fetchList: () => ACCOUNTING_FETCHERS[failureKey](departmentId),
      setList,
      setLoadFailures,
      failureKey,
      isCurrent,
    });
  });
}

/**
 * THE expanded row's once-per-page reference-data load — productions, supplier
 * records, sub-departments, gags, and (for the two expansion hooks only) the
 * supplier-name suggestions.
 *
 * 🔑 **ONCE PER PAGE, NOT ONCE PER ROW.** These lists are identical for every
 * order on the page, so fetching them inside the expanded row would issue four
 * or five requests every time somebody opened one — ten rows, forty requests,
 * for four unchanging lists. That is an N+1 on the CLIENT, where
 * `server/tests/api/n-plus-1-sweep.test.js` cannot see it.
 *
 * 🔴 **`setSupplierSuggestions` OMITTED = FOUR-LIST MODE, AND THE FIFTH REQUEST
 * IS NOT ISSUED AT ALL.** That is `pages/Approvals.js`, which has never fetched
 * `/api/suppliers/name-suggestions` — *"a real divergence, not an omission"*.
 * The absence is a property of the ARGUMENTS, so the extraction could not quietly
 * even the counts up: the spread below adds no fifth promise in that mode.
 *
 * 🪤 **The shared `.catch` fires once for the whole `Promise.all`** — which is
 * exactly why each supplier entry catches for ITSELF (rule (b) above). Before
 * the per-entry catches, any one rejection abandoned every list at once.
 */
export function loadOrderReferenceLists({
  setProductions,
  setSuppliers,
  setSubDepartments,
  setGags,
  setSupplierSuggestions,
  setLoadFailures,
}) {
  const wantSuggestions = typeof setSupplierSuggestions === 'function';
  return Promise.all([
    fetchProductionsOrEmpty(),
    fetchSuppliersOrNull(),
    fetchSubDepartments(),
    fetchGagsOrEmpty(),
    ...(wantSuggestions ? [fetchSupplierSuggestionsOrNull()] : []),
  ])
    .then(([prods, supps, subDepts, gagResp, supplierNames]) => {
      setProductions(Array.isArray(prods) ? prods : []);
      setSuppliers(Array.isArray(supps) ? supps : []);
      setSubDepartments(Array.isArray(subDepts) ? subDepts : []);
      setGags(Array.isArray(gagResp?.gags) ? gagResp.gags : []);
      if (wantSuggestions) {
        setSupplierSuggestions(Array.isArray(supplierNames) ? supplierNames : []);
      }
      // Records and suggestions report under the ONE `suppliers` key — the two
      // tiers merge into one picker, so "incomplete" is the only distinction
      // the person typing a supplier name could act on. Variadic, so the
      // four-list caller asks only about the tier it loaded.
      setLoadFailures((prev) => ({
        ...prev,
        suppliers: wantSuggestions ? suppliersFailed(supps, supplierNames) : suppliersFailed(supps),
      }));
    })
    .catch(() => setLoadFailures((prev) => ({ ...prev, suppliers: true })));
}
